/* eslint-disable max-statements */
import { graphql } from 'gatsby';
import { kebabCase } from 'lodash';
import React, { FC } from 'react';

import BodyRender from '~/components/BodyRender';
import { BodyRenderExternalData } from '~/components/BodyRender/models';
import LoginRegisterBlocks from '~/components/LoginRegisterBlocks';
import { PAGE_ONLY_FOR_SIGNED_IN } from '~/components/LoginRegisterBlocks/constants';
import { mapContentstackTranslations } from '~/context/TranslationContext/mapper';
import { useAuthGuard } from '~/hooks/useAuthGuard';
import { Layout } from '~/layout/Layout';
import { addBrandPostfixConditionally } from '~/utils/addBrandPostfixConditionally';
import { renderedItemsHaveLoginRegister } from '~/utils/bodyRenderHelpers';
import withContentstackPreview from '~/utils/livePreview';

import { ContentPageData } from './models';

const BASE_CLASS = 'l2-content-page';

export const L2ContentPage: FC<ContentPageData> = ({
  data: {
    profile,
    contentstackL2ContentPage,
    contentstackSearch,
    contentstackHeader,
    contentstackTranslations,
    contentstackHeaderWithSplitter,
    contentstackNewFooter,
    contentstackExtendedHeader,
    contentstackFooterWithSplitter,
    contentstackInfoBar,
    allContentstackItemListingGrid: { nodes: itemListingGrids },
    allContentstackAssets: { nodes: jsonRTEImages },
  },
  pageContext: { uid, locale, seoTitle, brand, fullPath },
}) => {
  if (typeof window === 'undefined')
    console.info('Building:', JSON.stringify({ uid, locale, seoTitle }));

  const t = mapContentstackTranslations(contentstackTranslations);

  const {
    seo,
    disabledLayoutBackground,
    pageHeader,
    fields,
    body,
    categories,
    openGraph,
  } = contentstackL2ContentPage;

  const bodyHasLoginResister = renderedItemsHaveLoginRegister(body);

  const onlyForSignedIn =
    process.env.GATSBY_PROTECTED_PAGES_ENABLED === 'true' &&
    bodyHasLoginResister;

  const { isLoggedIn } = useAuthGuard(onlyForSignedIn);

  const isAuthPending = onlyForSignedIn && isLoggedIn === null;
  const isAccessDenied = onlyForSignedIn && !isLoggedIn;

  if (isAuthPending) return null;

  if (isAccessDenied) {
    return <LoginRegisterBlocks variant={PAGE_ONLY_FOR_SIGNED_IN} />;
  }

  const categoryClasses = categories.map(
    ({ title }) => `${BASE_CLASS}--${kebabCase(title)}`
  );
  const mainImagePath =
    pageHeader.imageElement[0]?.image &&
    'url' in pageHeader.imageElement[0].image
      ? pageHeader.imageElement[0].image.url
      : undefined;

  const externalData: BodyRenderExternalData = {
    locale,
    itemListingGrids,
    jsonRTEImages,
  };

  return (
    <Layout
      {...{
        openGraph,
        mainImagePath,
        seo,
        searchWithChips: contentstackSearch,
        disabledLayoutBackground,
        headerBasic: contentstackHeader,
        extendedHeaderBasic: contentstackExtendedHeader,
        translations: t,
        headerWithSplitter: contentstackHeaderWithSplitter,
        headerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        footerBasic: contentstackNewFooter,
        footerWithSplitter: contentstackFooterWithSplitter,
        footerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        pageUid: uid,
        locale,
        brand,
        pageHeaderProps: { pageHeader, fields },
        className: [BASE_CLASS, ...categoryClasses].join(' '),
        infoBar: {
          ...contentstackInfoBar,
        },
        fullPath,
        profile,
      }}
    >
      <BodyRender
        items={body}
        externalData={externalData}
        currentPageUid={uid}
      />
    </Layout>
  );
};

export const query = graphql`
  query L2ContentPage(
    $id: String
    $locale: String
    $brand: [String]
    $jsonRTEImgUids: [String]
  ) {
    profile: contentstackProfile {
      ...ProfileFragment
    }
    contentstackSearch {
      chips
      searchButtonText: search_button_text
      searchInputPlaceholder: search_input_placeholder
      searchLabel: search_label
      searchResultLink: search_result_link {
        url
      }
    }
    contentstackL2ContentPage(id: { eq: $id }) {
      uid
      updated_at
      publish_details {
        time
      }
      fields {
        ...PageFieldsFragment
      }
      seo {
        ...SeoFragment
      }
      openGraph: open_graph {
        ...OpenGraphFragment
      }
      disabledLayoutBackground: disabled_layout_background
      pageHeader: page_header {
        ...PageHeaderFragment
      }
      body {
        ...BodyFragment
      }
      categories {
        title
      }
    }
    allContentstackItemListingGrid {
      nodes {
        ...ItemListingGridFragment
      }
    }
    allContentstackAssets(filter: { uid: { in: $jsonRTEImgUids } }) {
      nodes {
        uid
        url
        gatsbyImageData(layout: FULL_WIDTH, quality: 80)
      }
    }
    contentstackHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderBasicFragment
    }
    contentstackTranslations(publish_details: { locale: { eq: $locale } }) {
      ...Translations
    }
    contentstackExtendedHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...ExtendedHeaderBasicFragment
    }
    contentstackHeaderWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderWithSplitterFragment
    }
    contentstackSettings(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...SettingsFragment
    }
    contentstackInfoBar(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...InfoBarFragment
    }
    contentstackFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterBasicFragment
    }
    contentstackNewFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...NewFooterBasicFragment
    }
    contentstackFooterWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterWithSplitterFragment
    }
  }
`;

export default withContentstackPreview(
  L2ContentPage,
  addBrandPostfixConditionally(
    'l2_content_page',
    process.env.GATSBY_CONTENTSTACK_BRAND_TAG
  )
);

export { Head } from '~/common/StructuredData';
